/**
 * Why the agent left an inbound without a reply — the `reason` of a `reply_skipped` marker.
 *
 * Client-safe: the quality page groups missed replies by these, so the constants live apart
 * from the marker writer, which builds a Supabase client as it loads.
 */

import { SILENT_HANDOVER_SKIP_REASON } from '@/lib/conversation/conversation-events'

export const SKIP_REASONS = {
  AI_PAUSED_OR_DISABLED: 'ai_paused_or_disabled',
  MESSAGING_WINDOW_EXPIRED: 'messaging_window_expired',
  /** The model decided not to reply; the marker carries its wording in `reason` and an `ai_skip` flag. */
  AI_SKIP_REPLY: 'ai_skip_reply',
  /** A reply was due and never reached the customer, and nobody decided so: an empty answer or a failed send. */
  REPLY_NOT_DELIVERED: 'reply_not_delivered',
  AI_PAUSED_PRE_SEND: 'ai_paused_pre_send',
  NON_ACTIONABLE: 'non_actionable_message',
  DEBOUNCE_RECOVERY_EXPIRED: 'debounce_recovery_expired',
  /** Contact is on the agent's AI exclusion list — paused pre-LLM. */
  CONTACT_EXCLUSION: 'contact_exclusion',
  /** Silent handover: escalated with deliberately no customer-facing text. */
  SILENT_HANDOVER: SILENT_HANDOVER_SKIP_REASON,
  /** Dry run: reply stored for the operator but never delivered to the customer. */
  DRY_RUN: 'dry_run',
  /** Wallet depleted or spending cap reached — the tenant is alerted, the customer hears nothing. */
  AI_ACCESS_BLOCKED: 'ai_access_blocked',
  /** The workspace itself is deactivated: the message is stored, nothing else runs. */
  TENANT_INACTIVE: 'tenant_inactive',
} as const

export type SkipReason = (typeof SKIP_REASONS)[keyof typeof SKIP_REASONS]

/** A follow-up the scheduler withheld leaves the same marker, its reason behind this prefix. */
export const FOLLOW_UP_MARKER_PREFIX = 'follow_up_'

/** A missed reply is one that was owed; a non-actionable message is counted apart, not as a cause. */
type MissedReplyCause = Exclude<SkipReason, typeof SKIP_REASONS.NON_ACTIONABLE>

/** How each cause reads on the quality page; `hint` only where the label alone would not explain it. */
export const SKIP_REASON_DISPLAY: Record<MissedReplyCause, { label: string; hint?: string }> = {
  ai_paused_or_disabled: { label: 'AI paused or disabled' },
  messaging_window_expired: {
    label: 'Messaging window expired',
    hint: 'The channel no longer allowed a reply to this customer.',
  },
  ai_skip_reply: { label: 'The agent chose not to reply' },
  reply_not_delivered: {
    label: 'Reply not delivered',
    hint: 'A reply was due but never reached the customer.',
  },
  ai_paused_pre_send: {
    label: 'Paused before sending',
    hint: 'A person paused the conversation while the reply was being written.',
  },
  debounce_recovery_expired: {
    label: 'Reply window missed',
    hint: 'The reply was delayed past the point where sending it still made sense.',
  },
  contact_exclusion: { label: 'Contact excluded from AI' },
  silent_handover: {
    label: 'Silent handover',
    hint: 'Handed to the team without a customer-facing reply.',
  },
  dry_run: {
    label: 'Dry run',
    hint: 'The reply was written for the operator only.',
  },
  ai_access_blocked: {
    label: 'AI access blocked',
    hint: 'The wallet was empty or the spending cap reached.',
  },
  tenant_inactive: { label: 'Workspace deactivated' },
}

/** A cause the page has no words for — an older marker, or a channel's own reason. */
export function skipReasonDisplay(cause: string): { label: string; hint?: string } {
  return (
    SKIP_REASON_DISPLAY[cause as MissedReplyCause] ?? {
      label: cause.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    }
  )
}
